import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LinhaGantt } from '../../api/projetos.js';
import { atividadesApi } from '../../api/atividades.js';

interface PainelDetalheAtividadeProps {
  linha: LinhaGantt;
  projetoId: string;
  onFechar: () => void;
}

// SPEC-UX §10.1 "Interações": painel lateral ao clicar na barra — trilho de
// estado (fica para quando o painel tiver acesso à Atividade completa, não
// só LinhaGantt — a leitura atual do Gantt não traz estado com transições
// disponíveis; v1 do painel foca no apontamento inline, que é o requisito
// testável da spec) + apontamento +1h/+2h.
export function PainelDetalheAtividade({ linha, projetoId, onFechar }: PainelDetalheAtividadeProps) {
  const queryClient = useQueryClient();

  const apontar = useMutation({
    mutationFn: (horas: number) => atividadesApi.apontar(linha.atividadeId, 'sistema', horas, new Date().toISOString().slice(0, 10)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projeto', projetoId, 'gantt'] }),
  });

  return (
    <div className="fixed inset-y-0 right-0 z-40 flex w-80 flex-col gap-4 border-l border-borda bg-superficie p-5 shadow-lg">
      <div className="flex items-start justify-between">
        <div>
          {linha.grupo && <p className="fonte-mono text-xs text-grafite">{linha.grupo}</p>}
          <h3 className="text-sm font-semibold text-ink">{linha.atividadeNome}</h3>
        </div>
        <button type="button" onClick={onFechar} className="text-grafite hover:text-ink" aria-label="Fechar">
          ✕
        </button>
      </div>

      <div className="flex flex-col gap-1.5 text-sm">
        <div className="flex justify-between">
          <span className="text-grafite">Status</span>
          <span className="text-ink">{linha.estado}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-grafite">Início previsto</span>
          <span className="fonte-mono text-ink">{linha.inicioPrevisto}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-grafite">Fim previsto</span>
          <span className="fonte-mono text-ink">{linha.fimPrevisto}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-grafite">% concluído</span>
          <span className="fonte-mono text-ink">{Math.round(linha.pctDerivado * 100)}%</span>
        </div>
        {linha.conflito && <p className="mt-1 text-xs text-status-ambar">⚠ Conflito de precedência (R-ATV-8) — não bloqueia.</p>}
      </div>

      <div>
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-grafite">Apontar hora</p>
        <div className="flex gap-1.5">
          <button
            type="button"
            disabled={apontar.isPending}
            onClick={() => apontar.mutate(1)}
            className="rounded border border-aco/40 px-2.5 py-1 text-xs text-aco hover:bg-aco/5 disabled:opacity-50"
          >
            +1h
          </button>
          <button
            type="button"
            disabled={apontar.isPending}
            onClick={() => apontar.mutate(2)}
            className="rounded border border-aco/40 px-2.5 py-1 text-xs text-aco hover:bg-aco/5 disabled:opacity-50"
          >
            +2h
          </button>
        </div>
      </div>
    </div>
  );
}
